import { AUDIO_ASSETS, type AudioKey } from './audio.gen';

/**
 * Shared Web Audio plumbing for SFX and music: one AudioContext, a master gain per bus,
 * format choice (Opus in Ogg, else AAC in MP4), lazy buffer loading, gesture unlock and tab visibility.
 * Nothing here decides *whether* to play — sound.ts / music.ts check fxConfig first.
 */
type AC = AudioContext;
let ac: AC | null = null;
let sfxBus: GainNode | null = null;
let musicBus: GainNode | null = null;
let sfxVolume = 1;
let musicVolume = 0.8;

export function ctx(): AC | null {
  if (typeof window === 'undefined') return null;
  // A context made before the first gesture starts suspended and Chrome logs
  // "The AudioContext was not allowed to start": wait for the gesture (listeners stay armed).
  if (!ac) {
    installAudioListeners();
    if (!hasGesture()) return null;
  }
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ac) {
    try {
      ac = new Ctor();
    } catch {
      return null;
    }
    installAudioListeners();
  }
  if (ac.state === 'suspended' && unlocked && !hiddenPaused) void ac.resume().catch(() => undefined);
  return ac;
}

/** The current context without creating one. */
export function currentCtx(): AC | null {
  return ac;
}

export function sfxOut(c: AC): AudioNode {
  if (!sfxBus) {
    sfxBus = c.createGain();
    sfxBus.gain.value = sfxVolume;
    sfxBus.connect(c.destination);
  }
  return sfxBus;
}

export function musicOut(c: AC): AudioNode {
  if (!musicBus) {
    musicBus = c.createGain();
    musicBus.gain.value = musicVolume;
    musicBus.connect(c.destination);
  }
  return musicBus;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

export function setBusVolume(bus: 'sfx' | 'music', v: number): void {
  const val = clamp01(v);
  if (bus === 'sfx') sfxVolume = val;
  else musicVolume = val;
  const node = bus === 'sfx' ? sfxBus : musicBus;
  if (node && ac) node.gain.setTargetAtTime(val, ac.currentTime, 0.05);
}

export function busVolume(bus: 'sfx' | 'music'): number {
  return bus === 'sfx' ? sfxVolume : musicVolume;
}

// ---- format ----------------------------------------------------------------------------------------------

let format: 'ogg' | 'm4a' | null = null;

/** Opus in Ogg where the browser can play it (Chrome, Firefox, Edge, new Safari), otherwise AAC in MP4. */
export function audioFormat(): 'ogg' | 'm4a' {
  if (format) return format;
  let ogg = '';
  try {
    ogg = document.createElement('audio').canPlayType('audio/ogg; codecs="opus"');
  } catch {
    ogg = '';
  }
  format = ogg === 'probably' || ogg === 'maybe' ? 'ogg' : 'm4a';
  return format;
}

export function assetUrl(key: AudioKey): string {
  const a = AUDIO_ASSETS[key];
  return audioFormat() === 'ogg' ? a.ogg : a.m4a;
}

// ---- buffers ---------------------------------------------------------------------------------------------

let offline: BaseAudioContext | null = null;
/** Decoding before the first gesture uses an OfflineAudioContext (no autoplay warning); buffers work in any context. */
function decoder(): BaseAudioContext | null {
  if (offline) return offline;
  if (typeof window === 'undefined') return null;
  const Off =
    window.OfflineAudioContext ??
    (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  if (Off) {
    try {
      offline = new Off(1, 1, 44100);
      return offline;
    } catch {
      /* fall through */
    }
  }
  return ctx();
}

type Slot = AudioBuffer | Promise<AudioBuffer | null> | 'error';
const buffers = new Map<AudioKey, Slot>();

/** A decoded buffer if it is ready (never triggers loading). */
export function readyBuffer(key: AudioKey): AudioBuffer | null {
  const b = buffers.get(key);
  return b && typeof b === 'object' && !(b instanceof Promise) ? b : null;
}

/** Fetch + decode once; failures are remembered (the caller stays on synthesis / silence). */
export function loadBuffer(key: AudioKey): Promise<AudioBuffer | null> {
  const cur = buffers.get(key);
  if (cur === 'error') return Promise.resolve(null);
  if (cur instanceof Promise) return cur;
  if (cur) return Promise.resolve(cur);
  const c = ac ?? decoder();
  if (!c || !(key in AUDIO_ASSETS) || typeof fetch !== 'function') return Promise.resolve(null);
  const p = (async () => {
    try {
      const res = await fetch(assetUrl(key));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.arrayBuffer();
      const buf = await c.decodeAudioData(data);
      buffers.set(key, buf);
      return buf;
    } catch {
      buffers.set(key, 'error');
      return null;
    }
  })();
  buffers.set(key, p);
  return p;
}

/** Drop a decoded buffer (music tracks are large). */
export function dropBuffer(key: AudioKey): void {
  if (readyBuffer(key)) buffers.delete(key);
}

// ---- gesture unlock & visibility ----------------------------------------------------------------------

let unlocked = false;
let hiddenPaused = false;
let installed = false;
const unlockListeners = new Set<() => void>();

/** True after the first pointerdown / keydown: browsers only let audio start after a user gesture. */
export function isUnlocked(): boolean {
  return hasGesture();
}

export function onUnlock(fn: () => void): () => void {
  unlockListeners.add(fn);
  return () => unlockListeners.delete(fn);
}

/** The page has had a user gesture (ours, or one before the listeners were armed — `navigator.userActivation`). */
export function hasGesture(): boolean {
  if (unlocked) return true;
  if (typeof navigator !== 'undefined' && navigator.userActivation?.hasBeenActive) {
    unlock();
    return true;
  }
  return false;
}

function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  window.removeEventListener('pointerdown', unlock, true);
  window.removeEventListener('keydown', unlock, true);
  if (ac?.state === 'suspended' && !hiddenPaused) void ac.resume().catch(() => undefined);
  for (const fn of [...unlockListeners]) fn();
}

function onVisibility(): void {
  if (!ac) return;
  if (document.hidden) {
    if (ac.state === 'running') {
      hiddenPaused = true;
      void ac.suspend().catch(() => undefined);
    }
  } else if (hiddenPaused) {
    hiddenPaused = false;
    if (unlocked) void ac.resume().catch(() => undefined);
  }
}

/** Idempotent: gesture listeners (until the first gesture) and the tab-visibility pause. */
export function installAudioListeners(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  if (!unlocked) {
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
  }
  document.addEventListener('visibilitychange', onVisibility);
}

/** Tests only: forget the context, buffers and listeners. */
export function __resetAudioCore(): void {
  if (installed) {
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
    document.removeEventListener('visibilitychange', onVisibility);
  }
  ac = null;
  offline = null;
  sfxBus = null;
  musicBus = null;
  sfxVolume = 1;
  musicVolume = 0.8;
  format = null;
  buffers.clear();
  unlocked = false;
  hiddenPaused = false;
  installed = false;
  unlockListeners.clear();
}
